/**
 * TanStack Query Persistence Layer
 * Persists select query data to IndexedDB for cache-first rendering.
 * Only high-traffic, non-sensitive data is persisted.
 */

import type { PersistedClient, Persister, PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { get, set, del } from 'idb-keyval';
import { PERSISTENCE } from '@/lib/constants';
import { queryKeys } from '@/utils/queryKeys';

/**
 * Query key prefixes allowed for IndexedDB persistence.
 * Everything else (auth, notifications, ephemeral state) stays in-memory only.
 */
const PERSISTED_KEY_PREFIXES = [
  queryKeys.user.postsPrefix()[0],  // profile posts (infinite)
  queryKeys.square.feed()[0],       // square feed (infinite)
] as const;

/**
 * Multi-segment key patterns for persistence.
 * Matches query keys like ['profile', 'username', '<name>'].
 */
const PERSISTED_KEY_PATTERNS = [
  ['profile', 'username'],  // profile-by-username
] as const;

function matchesPattern(queryKey: readonly unknown[], pattern: readonly string[]): boolean {
  if (queryKey.length < pattern.length) return false;
  return pattern.every((segment, i) => queryKey[i] === segment);
}

function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return false;

  const firstKey = queryKey[0];
  if (typeof firstKey === 'string' && (PERSISTED_KEY_PREFIXES as readonly string[]).includes(firstKey)) {
    return true;
  }

  return PERSISTED_KEY_PATTERNS.some(pattern => matchesPattern(queryKey, pattern));
}

/**
 * IndexedDB persister using idb-keyval.
 * Implements the Persister interface from @tanstack/react-query-persist-client.
 */
function createIDBPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(PERSISTENCE.IDB_KEY, client);
    },
    restoreClient: async () => {
      return await get<PersistedClient>(PERSISTENCE.IDB_KEY);
    },
    removeClient: async () => {
      await del(PERSISTENCE.IDB_KEY);
    },
  };
}

/**
 * Persist options to pass to PersistQueryClientProvider.
 */
export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister: createIDBPersister(),
  maxAge: PERSISTENCE.MAX_AGE,
  buster: PERSISTENCE.BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // Only persist successful queries matching our allow-list
      if (query.state.status !== 'success') return false;
      return shouldPersistQuery(query.queryKey);
    },
  },
};
